<script>
import signaturesAPI from "@/services/signatures/signaturesAPI"
import Swal from "sweetalert2"
import { VueSignaturePad } from "vue-signature-pad"

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",")
  const mime = meta.match(/:(.*?);/)[1]
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

export default {
  name: "SignaturesManager",
  components: { VueSignaturePad },
  props: {
    companyId: { type: [Number, String], required: true },
  },
  data() {
    return {
      signatures: [],
      loading: true,
      showForm: false,
      mode: "upload",
      form: {
        signer_name: "",
        signer_document: "",
        signer_license: "",
        signer_position: "Responsable SG-SST",
        image: null,
      },
      uploading: false,
    }
  },
  computed: {
    canSave() {
      return this.form.signer_name && this.form.image
    },
    hasSignature() {
      return this.signatures.length > 0
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await signaturesAPI.index(this.companyId)
        this.signatures = (res && res.data) || []
      } finally {
        this.loading = false
      }
    },
    onFile(event) {
      this.form.image = event.target.files[0]
    },
    setMode(mode) {
      this.mode = mode
      this.form.image = null
      if (mode === "draw") {
        this.$nextTick(() => {
          if (this.$refs.signaturePad) this.$refs.signaturePad.resizeCanvas()
        })
      }
    },
    clearDrawing() {
      if (this.$refs.signaturePad) this.$refs.signaturePad.clearSignature()
      this.form.image = null
    },
    captureDrawing() {
      if (!this.$refs.signaturePad) return
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature("image/png")
      if (isEmpty) {
        Swal.fire("Atención", "Dibuja la firma antes de continuar", "info")
        return
      }
      const blob = dataUrlToBlob(data)
      this.form.image = new File([blob], `firma-${Date.now()}.png`, { type: "image/png" })
      Swal.fire({
        icon: "success", title: "Firma capturada",
        timer: 1000, showConfirmButton: false,
      })
    },
    resetForm() {
      this.form = {
        signer_name: "",
        signer_document: "",
        signer_license: "",
        signer_position: "Responsable SG-SST",
        image: null,
      }
      this.mode = "upload"
    },
    async save() {
      if (!this.canSave) return
      this.uploading = true
      try {
        const res = await signaturesAPI.store(this.companyId, this.form)
        if (res && res.data) {
          this.signatures.unshift(res.data)
          Swal.fire({
            icon: "success", title: "Firma registrada",
            timer: 1500, showConfirmButton: false,
          })
          this.showForm = false
          this.resetForm()
        } else {
          Swal.fire("Error", (res && res.message) || "No se pudo guardar", "error")
        }
      } finally {
        this.uploading = false
      }
    },
    async remove(sig) {
      const ok = await Swal.fire({
        icon: "warning", title: "¿Desactivar firma?",
        text: `${sig.signer_name} dejará de firmar los documentos de la plataforma.`,
        showCancelButton: true,
      })
      if (!ok.isConfirmed) return
      await signaturesAPI.destroy(sig.id)
      this.signatures = this.signatures.filter(s => s.id !== sig.id)
    },
  },
}
</script>

<template>
  <div class="card">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <h5 class="card-title">Firma del Responsable SG-SST</h5>
          <p class="text-muted small mb-0">
            Firma electrónica del Responsable del SG-SST de la empresa. Es la única que la
            plataforma estampa automáticamente en los documentos generados. Los demás firmantes
            (gerente, COPASST, etc.) firman por fuera de la app sobre las líneas en blanco.
          </p>
        </div>
        <button
          v-if="!hasSignature || showForm"
          class="btn btn-sm btn-primary"
          @click="showForm = !showForm"
        >
          {{ showForm ? "Cancelar" : "+ Registrar firma" }}
        </button>
      </div>

      <div v-if="showForm" class="sm-form mt-3 p-3 border rounded">
        <div class="form-group">
          <label>Nombre completo</label>
          <input v-model="form.signer_name" class="form-control" />
        </div>
        <div class="row">
          <div class="col-md-6 form-group">
            <label>Cédula</label>
            <input v-model="form.signer_document" class="form-control" />
          </div>
          <div class="col-md-6 form-group">
            <label>Número de licencia SST</label>
            <input v-model="form.signer_license" class="form-control" placeholder="Ej: 12345 de 2020" />
          </div>
        </div>
        <div class="form-group">
          <label>Cargo</label>
          <input v-model="form.signer_position" class="form-control" placeholder="Responsable SG-SST" />
        </div>

        <div class="form-group">
          <label class="d-block">Firma</label>
          <div class="btn-group btn-group-sm mb-2" role="group">
            <button type="button" class="btn"
              :class="mode === 'upload' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setMode('upload')">
              📷 Subir imagen
            </button>
            <button type="button" class="btn"
              :class="mode === 'draw' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setMode('draw')">
              ✏️ Dibujar firma
            </button>
          </div>

          <div v-if="mode === 'upload'">
            <input type="file" accept="image/png,image/jpeg" class="form-control-file" @change="onFile" />
            <div class="small text-muted mt-1">PNG transparente recomendado.</div>
          </div>

          <div v-else>
            <div class="sm-canvas-wrap">
              <VueSignaturePad
                ref="signaturePad"
                width="100%"
                height="180px"
                :options="{ penColor: '#111', minWidth: 1, maxWidth: 2.5 }"
              />
            </div>
            <div class="d-flex justify-content-between mt-2">
              <button type="button" class="btn btn-light btn-sm" @click="clearDrawing">
                ↺ Limpiar
              </button>
              <button type="button" class="btn btn-outline-success btn-sm" @click="captureDrawing">
                ✓ Usar esta firma
              </button>
            </div>
            <div v-if="form.image" class="small text-success mt-1">
              ● Firma capturada — lista para guardar
            </div>
            <div v-else class="small text-muted mt-1">
              Dibuja con el mouse o el dedo en tablet, luego click "Usar esta firma".
            </div>
          </div>
        </div>

        <button class="btn btn-primary btn-sm" :disabled="!canSave || uploading" @click="save">
          <span v-if="uploading"><b-spinner small></b-spinner></span>
          <span v-else>Guardar firma</span>
        </button>
      </div>

      <hr />

      <div v-if="loading" class="text-center my-3">
        <b-spinner small variant="primary"></b-spinner>
      </div>

      <div v-else-if="!hasSignature" class="text-muted small text-center my-3">
        Aún no hay firma registrada para el Responsable SG-SST.
      </div>

      <div v-else class="sm-list">
        <div v-for="sig in signatures" :key="sig.id" class="sm-item">
          <img v-if="sig.image_url" :src="sig.image_url" class="sm-img" />
          <div class="sm-meta">
            <strong>{{ sig.signer_name }}</strong>
            <div class="small text-muted">
              {{ sig.signer_position || "Responsable SG-SST" }}
              <span v-if="sig.signer_document"> · CC {{ sig.signer_document }}</span>
              <span v-if="sig.signer_license"> · Lic. SST {{ sig.signer_license }}</span>
            </div>
          </div>
          <button class="btn btn-sm btn-outline-danger" @click="remove(sig)">×</button>
        </div>
        <p class="text-muted small mb-0 mt-1">
          Solo se admite una firma. Desactiva la actual para reemplazarla.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sm-list { display: flex; flex-direction: column; gap: 8px; }
.sm-item {
  display: flex; align-items: center; gap: 12px;
  padding: 8px; border: 1px solid #eee; border-radius: 6px;
}
.sm-img { max-height: 40px; max-width: 120px; }
.sm-meta { flex: 1; }
.sm-canvas-wrap {
  border: 1px dashed #aaa;
  border-radius: 6px;
  background: #fafafa;
  overflow: hidden;
}
</style>
